<script setup>
import DpIcon from '../DpIcon';
import DpButton from "../DpButton";
import DpExpand from '../DpExpand';
import { ref, reactive, computed, onMounted, watch, inject } from 'vue';
import {useEnvStore, useCompanyStore} from '@dutypay/store-modules'
import { isEmpty } from "lodash";
import {to} from "await-to-js";
import {storeToRefs} from 'pinia';
import { interceptor } from "@dutypay/utilities";
import {useI18n} from 'vue-i18n'
const { t } = useI18n()
const router = inject('router');

const tKAAliases = [
    t('activeApplication.imageAltTag.displayValues'),
    t('activeApplication.caption.displayValues'),
    t('availableApplication.imageAltTag.displayValues'),
    t('availableApplication.caption.displayValues'),
    t('availableApplication.description.displayValues')
]

const props = defineProps({
  closeHandler: {type: Function}
})
const composables = inject('composables')
const { isMobile } = composables.useViewportSize();
const emit = defineEmits(['hasApplications'])
const menuSettings = reactive({
  active: [],
  available: []
})
const isAvailableApplicationsVisible = ref(false);
const isLoading = ref(false);

const envStore = useEnvStore()
const companyStore = useCompanyStore()

const assetsApiEndpoint = computed(() => {
  return `${envStore.apiUrls.dutypay}/api/v0/assets`;
});
const hasActiveApplications = computed(() => {
  return (menuSettings.active.length > 0);
});
const hasAvailableApplications = computed(() => {
  return (menuSettings.available.length > 0);
});

async function getMenuSettings() {
  if (!companyStore.hasSelectedCompany || isEmpty(envStore.apiUrls))
    return;
  let err, res;
  [err, res] = await to(interceptor({
    url: `${envStore.apiUrls.dutypay}/api/v0/application-switch/`,
    method: 'POST',
    data: {
      "decision-tree-id": "7926389e-bb20-4470-b02e-cfd20d19010f",
      "params": {
        "Company": {
          "sp": {
            "company": {
              "id": companyStore.selectedCompany.sp.company.id
            }
          },
          "hs": {
            "company": {
              "id": companyStore.selectedCompany.hs.company.id
            }
          }
        }
      }
    }
  }));
  if (!isEmpty(res.data.result.active) || !isEmpty(res.data.result.available)) {
    emit('hasApplications')
  }
  menuSettings.active = res.data.result.active;
  menuSettings.available = res.data.result.available;
  if(err){
    throw err;
  }
}
onMounted(async () => {
  await getMenuSettings();
})
const { selectedCompany } = storeToRefs(companyStore);
watch(selectedCompany, async () => {
  await getMenuSettings();
})

function hideAvailableApplications() {
  isAvailableApplicationsVisible.value = false;
}
function showAvailableApplications() {
  isAvailableApplicationsVisible.value = true;
}

async function openApplication(url) {
  isLoading.value = true;
  await envStore.switchApplication(url)
  isLoading.value = false;
  props.closeHandler();
}

async function goToStart() {
  await router.push({path: '/'});
  props.closeHandler();
}
</script>

<template>
  <div ref="applicationSwitch">
    <div v-if="hasActiveApplications || hasAvailableApplications" class="application-switch__wrapper">
      <DpButton purpose="secondaryAction" @click="goToStart" class="hidden-sm-and-up full-width">{{t('toStart.buttonLabel')}}</DpButton>
      <div class="application-switch__container" v-loading="isLoading"
           element-loading-background="rgba(0, 0, 0, 0.8)">
        <div v-if="hasActiveApplications">
          <span class="application-switch__category">{{t('activeApplications.label')}}</span>
          <div class="dp-flex row wrap justify-content__flex-start align-items__center">
            <div
                class="application-switch__items text-align-center"
                :class="{'is-mobile': isMobile}"
                v-for="(item, index) in menuSettings.active"
                @click="openApplication(item.url)"
                @keypress="openApplication(item.url)"
                :key="item.application + index.toString()"
                tabindex="0">
              <img :src="`${assetsApiEndpoint}/sp/${item.sp.hubSystemImage.src.id}/svg`"
                   :alt="t(`activeApplication.imageAltTag.displayValues-${item.sp.hubSystemImage.alt.id}`)"
                   class="application-switch__item-logo--active"/>
              <span class="application-switch__items__item-name">{{ t(`ds.components.DpHeader.ApplicationSwitchMenu.activeApplication.caption.displayValues-${item.sp.hubSystemImage.caption.id}`) }}</span>
            </div>
            <DpExpand>
              <div v-if="!isAvailableApplicationsVisible && hasAvailableApplications"
                   class="application-switch__items text-align-center dp-flex column"
                   :class="{'is-mobile': isMobile}"
                   @click="showAvailableApplications"
                   @keypress="showAvailableApplications"
                   tabindex="0">
                <DpIcon icon="add" style="font-size: 2em;"/><span class="block application-switch__items__item-name">{{t('exploreMore.buttonLabel')}}</span>
              </div>
            </DpExpand>
          </div>

        </div>
        <DpExpand>
          <div v-if="(hasAvailableApplications && isAvailableApplicationsVisible) || !hasActiveApplications">
            <span class="application-switch__category">{{t('availableApplications.label')}}
              <dp-button v-if="hasActiveApplications"
                 v-on:click="hideAvailableApplications"
                 v-on:keypress="hideAvailableApplications"
                 purpose="secondaryAction"
                 size="tiny"
                 tabindex="0"
                 style="margin-inline-start: .5em;">{{t('showLess.buttonLabel')}}</dp-button></span>
            <div class="dp-flex row wrap justify-content__flex-start align-items__flex-start">
              <div class="application-switch__items text-align-center"
                   :class="{'is-mobile': isMobile}"
                   v-for="(item, index) in menuSettings.available"
                   @click="openApplication(item.url)"
                   @keypress="openApplication(item.url)"
                   :key="item.application + index.toString()"
                   tabindex="0">
                <img :src="`${assetsApiEndpoint}/sp/${item.sp.hubSystemImage.src.id}/svg`"
                     :alt="t(`availableApplication.imageAltTag.displayValues-${item.sp.hubSystemImage.alt.id}`)"
                     class="application-switch__item-logo--available"/>
                <span class="application-switch__items__item-name">{{ t(`ds.components.DpHeader.ApplicationSwitchMenu.availableApplication.caption.displayValues-${item.sp.hubSystemImage.caption.id}`) }}</span>
                <span class="block application-switch__items__item-description">{{ t(`ds.components.DpHeader.ApplicationSwitchMenu.availableApplication.description.displayValues-${item.sp.hubSystemImage.description.id}`) }}</span>
              </div>
            </div>
          </div>
        </DpExpand>

        <div class="application-switch__close"
             @click="closeHandler"
             @keypress="closeHandler"
             tabindex="0">
          <DpIcon icon="close" :margin="false">
          </DpIcon>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.application-switch__wrapper {
  padding-bottom: 2rem;
  padding-top: 1rem;

  .application-switch__container {
    position: relative;
    padding-top: .9rem;
    padding-bottom: .4rem;
    padding-inline-start: 1.4rem;
    padding-inline-end: .9rem;
    border-radius: 10px;
    box-shadow: inset 3px 12px 24px rgba(0, 0, 0, .18);
    background-color: rgba(0, 0, 0, 0.75);
    overflow: hidden;

    .application-switch__close {
      position: absolute;
      inset-inline-end: 0.9rem;
      inset-block-start: 0.4rem;
      cursor: pointer;
      font-size: 2em;
      color: white;
    }

    .application-switch__category {
      font-size: 0.8rem;
      color: #FFFFFF;
      padding-bottom: 1rem;
      padding-top: .5rem;
      display: block;
    }

    .application-switch__items {
      padding: .8rem .7rem;
      border-radius: 8px;
      margin-inline-end: 1.6rem;
      margin-bottom: 1rem;
      display: inline-block;
      cursor: pointer;
      width: 8rem;
      color: #FFFFFF;

      &:hover, &.is-mobile {
        background-color: rgba(255, 255, 255, 0.1);
      }

      .application-switch__item-logo--active {
        width: auto;
        height: 5.6rem;
      }
      .application-switch__item-logo--available {
        width: auto;
        height: 4.6rem;
      }

      .application-switch__items__item-name {
      }

      .application-switch__items__item-description {
        font-size: 0.85rem;
        text-wrap: normal;
      }

      a {
        font-size: 0.714rem;
        color: #adadad;
      }

    }

  }
}
</style>

<i18n>
{
  "ar-AE": {
    "exploreMore": {
      "buttonLabel": "إكتشف أكثر"
    },
    "showLess": {
      "buttonLabel": "يخفي"
    },
    "availableApplications": {
      "label": "المحاور المتاحة"
    },
    "activeApplications": {
      "label": "المحاور النشطة"
    }
  },
  "ar-EG": {
    "exploreMore": {
      "buttonLabel": "إكتشف أكثر"
    },
    "showLess": {
      "buttonLabel": "يخفي"
    },
    "availableApplications": {
      "label": "المحاور المتاحة"
    },
    "activeApplications": {
      "label": "المحاور النشطة"
    }
  },
  "ar-SA": {
    "exploreMore": {
      "buttonLabel": "إكتشف أكثر"
    },
    "showLess": {
      "buttonLabel": "يخفي"
    },
    "availableApplications": {
      "label": "المحاور المتاحة"
    },
    "activeApplications": {
      "label": "المحاور النشطة"
    }
  },
  "de-DE": {
    "exploreMore": {
      "buttonLabel": "Mehr entdecken"
    },
    "showLess": {
      "buttonLabel": "Ausblenden"
    },
    "availableApplications": {
      "label": "Verfügbare Hubs"
    },
    "activeApplications": {
      "label": "Aktive Hubs"
    }
  },
  "de-AT": {
    "exploreMore": {
      "buttonLabel": "Mehr entdecken"
    },
    "showLess": {
      "buttonLabel": "Ausblenden"
    },
    "availableApplications": {
      "label": "Verfügbare Hubs"
    },
    "activeApplications": {
      "label": "Aktive Hubs"
    }
  },
  "de-CH": {
    "exploreMore": {
      "buttonLabel": "Mehr entdecken"
    },
    "showLess": {
      "buttonLabel": "Ausblenden"
    },
    "availableApplications": {
      "label": "Verfügbare Hubs"
    },
    "activeApplications": {
      "label": "Aktive Hubs"
    }
  },
  "en-US": {
    "exploreMore": {
      "buttonLabel": "Discover More"
    },
    "showLess": {
      "buttonLabel": "Hide"
    },
    "availableApplications": {
      "label": "Available Hubs"
    },
    "activeApplications": {
      "label": "Active Hubs"
    }
  },
  "en-GB": {
    "exploreMore": {
      "buttonLabel": "Discover More"
    },
    "showLess": {
      "buttonLabel": "Hide"
    },
    "availableApplications": {
      "label": "Available Hubs"
    },
    "activeApplications": {
      "label": "Active Hubs"
    }
  },
  "it-IT": {
    "exploreMore": {
      "buttonLabel": "Scopri di più"
    },
    "showLess": {
      "buttonLabel": "Nascondere"
    },
    "availableApplications": {
      "label": "Hub disponibili"
    },
    "activeApplications": {
      "label": "Hub attivi"
    }
  },
  "fr-FR": {
    "exploreMore": {
      "buttonLabel": "Découvrir plus"
    },
    "showLess": {
      "buttonLabel": "Cacher"
    },
    "availableApplications": {
      "label": "Hubs disponibles"
    },
    "activeApplications": {
      "label": "Hubs actifs"
    }
  },
  "es-ES": {
    "exploreMore": {
      "buttonLabel": "Descubrir más"
    },
    "showLess": {
      "buttonLabel": "Ocultar"
    },
    "availableApplications": {
      "label": "Hubs disponibles"
    },
    "activeApplications": {
      "label": "Hubs activos"
    }
  },
  "pl-PL": {
    "exploreMore": {
      "buttonLabel": "Dowiedz się więcej"
    },
    "showLess": {
      "buttonLabel": "Ukrywać"
    },
    "availableApplications": {
      "label": "Dostępne koncentratory"
    },
    "activeApplications": {
      "label": "Aktywne centra"
    }
  },
  "nl-NL": {
    "exploreMore": {
      "buttonLabel": "Ontdek meer"
    },
    "showLess": {
      "buttonLabel": "Verbergen"
    },
    "availableApplications": {
      "label": "Beschikbare hubs"
    },
    "activeApplications": {
      "label": "Actieve hubs"
    }
  },
  "bg-BG": {
    "exploreMore": {
      "buttonLabel": "Открий повече"
    },
    "showLess": {
      "buttonLabel": "Крия"
    },
    "availableApplications": {
      "label": "Налични хъбове"
    },
    "activeApplications": {
      "label": "Активни центрове"
    }
  },
  "bs-BA": {
    "exploreMore": {
      "buttonLabel": "Otkrijte više"
    },
    "showLess": {
      "buttonLabel": "Sakrij se"
    },
    "availableApplications": {
      "label": "Dostupna čvorišta"
    },
    "activeApplications": {
      "label": "Aktivna čvorišta"
    }
  },
  "da-DK": {
    "exploreMore": {
      "buttonLabel": "Opdag mere"
    },
    "showLess": {
      "buttonLabel": "Skjule"
    },
    "availableApplications": {
      "label": "Tilgængelige hubs"
    },
    "activeApplications": {
      "label": "Aktive hubs"
    }
  },
  "et-EE": {
    "exploreMore": {
      "buttonLabel": "Avastage rohkem"
    },
    "showLess": {
      "buttonLabel": "Peida"
    },
    "availableApplications": {
      "label": "Saadaval jaoturid"
    },
    "activeApplications": {
      "label": "Aktiivsed keskused"
    }
  },
  "fi-FI": {
    "exploreMore": {
      "buttonLabel": "Löydä lisää"
    },
    "showLess": {
      "buttonLabel": "Piilottaa"
    },
    "availableApplications": {
      "label": "Käytettävissä olevat keskittimet"
    },
    "activeApplications": {
      "label": "Aktiiviset keskittimet"
    }
  },
  "el-GR": {
    "exploreMore": {
      "buttonLabel": "Ανακάλυψε περισσότερα"
    },
    "showLess": {
      "buttonLabel": "Κρύβω"
    },
    "availableApplications": {
      "label": "Διαθέσιμοι κόμβοι"
    },
    "activeApplications": {
      "label": "Ενεργοί κόμβοι"
    }
  },
  "he-IL": {
    "exploreMore": {
      "buttonLabel": "לגלות עוד"
    },
    "showLess": {
      "buttonLabel": "להתחבא"
    },
    "availableApplications": {
      "label": "רכזות זמינות"
    },
    "activeApplications": {
      "label": "רכזות פעילות"
    }
  },
  "ga-IE": {
    "exploreMore": {
      "buttonLabel": "Faigh amach níos mó"
    },
    "showLess": {
      "buttonLabel": "Ceilt"
    },
    "availableApplications": {
      "label": "Moil ar Fáil"
    },
    "activeApplications": {
      "label": "Moil Ghníomhacha"
    }
  },
  "ja-JP": {
    "exploreMore": {
      "buttonLabel": "もっと発見する"
    },
    "showLess": {
      "buttonLabel": "隠れる"
    },
    "availableApplications": {
      "label": "利用可能なハブ"
    },
    "activeApplications": {
      "label": "アクティブハブ"
    }
  },
  "hr-HR": {
    "exploreMore": {
      "buttonLabel": "Otkrij više"
    },
    "showLess": {
      "buttonLabel": "Sakriti"
    },
    "availableApplications": {
      "label": "Dostupni koncentratori"
    },
    "activeApplications": {
      "label": "Aktivni koncentratori"
    }
  },
  "lv-LV": {
    "exploreMore": {
      "buttonLabel": "Atklājiet vairāk"
    },
    "showLess": {
      "buttonLabel": "Slēpt"
    },
    "availableApplications": {
      "label": "Pieejamie centrmezgli"
    },
    "activeApplications": {
      "label": "Aktīvie centrmezgli"
    }
  },
  "lt-LT": {
    "exploreMore": {
      "buttonLabel": "Atraskite daugiau"
    },
    "showLess": {
      "buttonLabel": "Slėpti"
    },
    "availableApplications": {
      "label": "Galimi centrai"
    },
    "activeApplications": {
      "label": "Aktyvūs centrai"
    }
  },
  "pt-PT": {
    "exploreMore": {
      "buttonLabel": "Descubra mais"
    },
    "showLess": {
      "buttonLabel": "Esconder"
    },
    "availableApplications": {
      "label": "Hubs disponíveis"
    },
    "activeApplications": {
      "label": "Hubs Ativos"
    }
  },
  "ro-RO": {
    "exploreMore": {
      "buttonLabel": "Descopera mai mult"
    },
    "showLess": {
      "buttonLabel": "Ascunde"
    },
    "availableApplications": {
      "label": "Hub-uri disponibile"
    },
    "activeApplications": {
      "label": "Hub-uri active"
    }
  },
  "ru-RU": {
    "exploreMore": {
      "buttonLabel": "Узнать больше"
    },
    "showLess": {
      "buttonLabel": "Скрывать"
    },
    "availableApplications": {
      "label": "Доступные концентраторы"
    },
    "activeApplications": {
      "label": "Активные концентраторы"
    }
  },
  "sv-SE": {
    "exploreMore": {
      "buttonLabel": "Upptäck mer"
    },
    "showLess": {
      "buttonLabel": "Dölj"
    },
    "availableApplications": {
      "label": "Tillgängliga hubbar"
    },
    "activeApplications": {
      "label": "Aktiva hubbar"
    }
  },
  "sk-SK": {
    "exploreMore": {
      "buttonLabel": "Objavte viac"
    },
    "showLess": {
      "buttonLabel": "Skryť"
    },
    "availableApplications": {
      "label": "Dostupné rozbočovače"
    },
    "activeApplications": {
      "label": "Aktívne centrá"
    }
  },
  "cs-CZ": {
    "exploreMore": {
      "buttonLabel": "Objevte více"
    },
    "showLess": {
      "buttonLabel": "Skrýt"
    },
    "availableApplications": {
      "label": "Dostupné rozbočovače"
    },
    "activeApplications": {
      "label": "Aktivní centra"
    }
  },
  "tr-TR": {
    "exploreMore": {
      "buttonLabel": "Daha fazlasını keşfet"
    },
    "showLess": {
      "buttonLabel": "Gizlemek"
    },
    "availableApplications": {
      "label": "Kullanılabilir Hub'lar"
    },
    "activeApplications": {
      "label": "Etkin Hub'lar"
    }
  },
  "hu-HU": {
    "exploreMore": {
      "buttonLabel": "Fedezz fel többet"
    },
    "showLess": {
      "buttonLabel": "Elrejt"
    },
    "availableApplications": {
      "label": "Elérhető elosztók"
    },
    "activeApplications": {
      "label": "Aktív központok"
    }
  },
  "fr-BE": {
    "exploreMore": {
      "buttonLabel": "Découvrir plus"
    },
    "showLess": {
      "buttonLabel": "Cacher"
    },
    "availableApplications": {
      "label": "Hubs disponibles"
    },
    "activeApplications": {
      "label": "Hubs actifs"
    }
  },
  "fr-LU": {
    "exploreMore": {
      "buttonLabel": "Découvrir plus"
    },
    "showLess": {
      "buttonLabel": "Cacher"
    },
    "availableApplications": {
      "label": "Hubs disponibles"
    },
    "activeApplications": {
      "label": "Hubs actifs"
    }
  },
  "mt-MT": {
    "exploreMore": {
      "buttonLabel": "Skopri aktar"
    },
    "showLess": {
      "buttonLabel": "Aħbi"
    },
    "availableApplications": {
      "label": "Hubs disponibbli"
    },
    "activeApplications": {
      "label": "Hubs attivi"
    }
  },
  "sl-SI": {
    "exploreMore": {
      "buttonLabel": "Odkrijte več"
    },
    "showLess": {
      "buttonLabel": "Skrij se"
    },
    "availableApplications": {
      "label": "Vozlišča, ki so na voljo"
    },
    "activeApplications": {
      "label": "Aktivna vozlišča"
    }
  },
  "el-CY": {
    "exploreMore": {
      "buttonLabel": "Ανακάλυψε περισσότερα"
    },
    "showLess": {
      "buttonLabel": "Κρύβω"
    },
    "availableApplications": {
      "label": "Διαθέσιμοι κόμβοι"
    },
    "activeApplications": {
      "label": "Ενεργοί κόμβοι"
    }
  },
  "tr-CY": {
    "exploreMore": {
      "buttonLabel": "Daha fazlasını keşfet"
    },
    "showLess": {
      "buttonLabel": "Gizlemek"
    },
    "availableApplications": {
      "label": "Kullanılabilir Hub'lar"
    },
    "activeApplications": {
      "label": "Etkin Hub'lar"
    }
  },
  "uk-UA": {
    "exploreMore": {
      "buttonLabel": "Дізнайтеся більше"
    },
    "showLess": {
      "buttonLabel": "Приховати"
    },
    "availableApplications": {
      "label": "Наявні хаби"
    },
    "activeApplications": {
      "label": "Активні концентратори"
    }
  },
  "zh-CN": {
    "exploreMore": {
      "buttonLabel": "发现更多"
    },
    "showLess": {
      "buttonLabel": "隐藏"
    },
    "availableApplications": {
      "label": "可用集线器"
    },
    "activeApplications": {
      "label": "活动集线器"
    }
  }
}
</i18n>

